import React, { useState, useEffect, useMemo, useCallback } from "react";
import * as Mui from "@mui/material";
import { makeStyles } from "tss-react/mui";
import { AppDatePicker, AppTimePicker } from "component/shared/date-picker";
import * as Icons from "react-feather";
import moment, { Moment } from "moment";
import { TaskType } from "type/task-type";
import { TaskStatus } from "type/task-status";
import { updateTask, createTask } from "redux/slice/tasks";
import * as yup from "yup";
import { validate } from "shared/yup";
import { getActiveLeads, getAgents, getUser } from "redux/selector";
import { Task } from "model/task";
import { Lead } from "model/lead";
import { CustomAutocomplete } from "component/shared/auto-complete";
import { agentTasksDev } from "shared/env";
import { useTheme } from "@mui/material/styles";
import { FormField, FormSelectField } from "component/shared/form-fields";
import { useSelector } from "react-redux";
import { useAppThunkDispatch } from "redux/store";
import { styles } from "./style";

interface Props {
	open: boolean;
	onClose: () => void;
	task?: Task | null;
	lead?: Lead;
}

const useStyles = makeStyles()(styles);

export const AddEditTaskDialog: React.FC<Props> = ({ open, onClose, task, lead }) => {
	const theme = useTheme();
	const { classes } = useStyles();
	const dispatch = useAppThunkDispatch();

	const user = useSelector(getUser);
	const leads = useSelector(getActiveLeads);
	const agents = useSelector(getAgents);

	const leadLabel = useMemo(() => {
		if (task && task.leadId) {
			const taskLead = leads.find(l => l.id === task.leadId);
			return taskLead?.label || "";
		}
		return "";
	}, [task, leads]);

	// State
	const [submitted, setSubmitted] = useState(false);
	const [title, setTitle] = useState("");
	const [description, setDescription] = useState("");
	const [leadInput, setLeadInput] = useState("");
	const [leadId, setLeadId] = useState(0);
	const [agentId, setAgentId] = useState<number | undefined>(0);
	const [taskTypeId, setTaskTypeId] = useState(0);
	const [priority, setPriority] = useState(false);
	const [startDate, setStartDate] = useState<Moment>(moment());
	const [startTime, setStartTime] = useState<Moment>(moment());
	const [dueDate, setDueDate] = useState<Moment | null>(null);
	const [dueTime, setDueTime] = useState<Moment | null>(null);

	// Reset form when dialog opens/closes
	useEffect(() => {
		if (!open) {
			setSubmitted(false);
			setTitle("");
			setDescription("");
			setLeadInput("");
			setLeadId(0);
			setAgentId(0);
			setTaskTypeId(0);
			setPriority(false);
			setStartDate(moment());
			setStartTime(moment());
			setDueDate(null);
			setDueTime(null);
		} else {
			if (task) {
				setTitle(task.title || "");
				setDescription(task.description || "");
				setLeadId(task.leadId || 0);
				setAgentId(user.admin ? task.agentId || 0 : user.agentId);
				setLeadInput(leadLabel || "");
				setTaskTypeId(task.type ? task.type.id : 0);
				setPriority(task.priority || false);
				setStartDate(task.startOn || moment());
				setStartTime(task.startOn || moment());
				setDueDate(task.endOn || null);
				setDueTime(task.endOn || null);
			} else if (lead) {
				setLeadId(lead.id);
				setLeadInput(lead.label);
			}
		}
	}, [open, task, lead, leadLabel, user]);

	// Computed values
	const combineDateAndTime = useCallback((date: Moment, time: Moment) => {
		return date.clone().set({
			hour: time.get('hour'),
			minute: time.get('minute'),
			second: 0,
			millisecond: 0,
		});
	}, []);

	const startOn = useMemo(() => combineDateAndTime(startDate, startTime), [startDate, startTime, combineDateAndTime]);
	const endOn = useMemo(() => dueDate && dueTime ? combineDateAndTime(dueDate, dueTime) : null, [dueDate, dueTime, combineDateAndTime]);

	const singleAgent = agents.length === 1;
	const assigner = task ? agents.find(agent => agent.id === task.creatorId) : undefined;
	const disabled = task ? !user.admin && task.creatorId !== user.agentId : false;

	// Validation schema
	const validationSchema = useMemo(() => ({
		title: yup.string().required("Title is required."),
		startDate: yup.mixed().required("Start date is required."),
		startTime: yup.mixed().required("Start time is required."),
		dueDate: yup
			.mixed()
			.test(
				"check-due-date",
				"Due date cannot be before start date.",
				(value) => {
					const date: Moment = value as Moment;
					if (date) {
						return date.format("YYYYMMDD") >= startDate.format("YYYYMMDD");
					}
					return true;
				}
			),
		dueTime: yup.mixed().required("End time is required."),
		endOn: yup
			.mixed()
			.test(
				"check-due-time",
				"Due date and time must be after start date and time.",
				(value) => {
					const date: Moment = value as Moment;
					if (date) {
						return date.isAfter(startOn);
					}
					return true;
				}
			)
			.required("Due date is required."),
		leadId: yup
			.number()
			.test(
				"check-lead-id",
				"Please select a valid lead.",
				(id) => {
					if (leadInput && id === 0) {
						return false;
					}
					return true;
				}
			),
		taskTypeId: yup
			.number()
			.moreThan(0, "Please select a task type.")
			.required("Task type is required."),
	}), [startDate, startOn, leadInput]);

	const errors = validate(validationSchema, {
		title,
		startDate,
		startTime,
		dueDate,
		dueTime,
		endOn,
		leadId,
		taskTypeId
	});

	// Handlers
	const handleSubmit = useCallback(() => {
		setSubmitted(true);
		const type = TaskType.getById(taskTypeId);
		const status = TaskStatus.ACTIVE;

		if (!errors && endOn && status) {
			if (task && task.id) {
				dispatch(updateTask({ task: {
					id: task.id,
					title,
					description: description || undefined,
					leadId: leadId || undefined,
					agentId: agentId || undefined,
					creatorId: user.agentId,
					type,
					status,
					priority,
					startOn,
					endOn,
					createdOn: task.createdOn,
					updatedOn: moment(),
				}}));
			} else {
				dispatch(createTask({ task: {
					title,
					description: description || undefined,
					leadId: leadId || undefined,
					agentId: user.admin ? agentId : user.agentId,
					creatorId: user.agentId,
					type,
					status,
					priority,
					startOn,
					endOn,
				}}));
			}
			onClose();
		}
	}, [
		errors, endOn, task, title, description, leadId, agentId, user, priority, startOn,
		taskTypeId, dispatch, onClose
	]);

	const isAdmin = useCallback((id: number | undefined) => {
		if (id) {
			return agents.find(agent => agent.id === id)?.admin;
		}
		return false;
	}, [agents]);

	return (
		<Mui.Dialog
			open={open}
			onClose={onClose}
			scroll="paper"
			maxWidth="sm"
			fullWidth
		>
			<Mui.DialogTitle className={classes.dialogTitle}>
				<Mui.Typography variant="h4" component="h2">
					{task ? (disabled ? "Assigned Task" : "Edit Task") : "Add Task"}
				</Mui.Typography>
				<Mui.IconButton
					className={classes.closeButton}
					onClick={onClose}
					size="small"
				>
					<Icons.X size={24} />
				</Mui.IconButton>
			</Mui.DialogTitle>
			<Mui.DialogContent dividers className={classes.dialogContent}>
				<div className={classes.formContainer}>
					<FormField
						label="Title"
						value={!disabled ? title : task?.title || ""}
						onChange={(e) => setTitle(e.target.value)}
						placeholder=""
						required
						error={submitted && !!errors?.title}
						helperText={submitted ? errors?.title : undefined}
					/>

					<FormField
						label="Description"
						value={!disabled ? description : task?.description || ""}
						onChange={(e) => setDescription(e.target.value)}
						placeholder=""
						multiline
						rows={3}
					/>

					<FormSelectField
						label="Task Type"
						value={!disabled ? taskTypeId : task?.type?.id || 0}
						onChange={value => setTaskTypeId(value || 0)}
						options={TaskType.values()}
						valueExtractor={type => type.id}
						labelExtractor={type => type.name}
						required
						error={submitted && !!errors?.taskTypeId}
						helperText={submitted ? errors?.taskTypeId : undefined}
					/>

					{!disabled ? (
						<CustomAutocomplete
							options={!agentId || isAdmin(agentId) ? leads : leads.filter(l => !l.agent?.id || l.agent.id === agentId)}
							label="Lead"
							optionLabelExtractor={(option) => option.label}
							fullWidth
							placeholder=""
							onInputChange={(_event, value, reason) => {
								if (reason !== "reset") {
									setLeadInput(value);
									setLeadId(0);
								}
							}}
							onChange={(_event, option) => {
								if (option && option.label) {
									setLeadId(option.id as number);
									setLeadInput(option.label);
									if (option.agent) {
										setAgentId(option.agent.id);
									}
								}
							}}
							error={submitted && !!errors?.leadId}
							helperText={submitted ? errors?.leadId : undefined}
							inputValue={leadInput}
						/>
					) : (
						<FormField
							label="Lead"
							value={leadLabel}
							onChange={() => {}}
						/>
					)}

					{user.admin && agentTasksDev && !singleAgent && (
						<FormSelectField
							label="Agent"
							value={agentId}
							onChange={value => {
								setAgentId(value);
								// Clear lead only if it has a different agent assigned (not unassigned)
								if (leadId && value) {
									const currentLead = leads.find(l => l.id === leadId);
									if (currentLead && currentLead.agent?.id && currentLead.agent.id !== value) {
										setLeadId(0);
										setLeadInput("");
									}
								}
							}}
							options={agents}
							valueExtractor={agent => agent.id}
							labelExtractor={(agent) => agent.name || agent.email || ""}
						/>
					)}

					<div className={classes.dateTimeRow}>
							<AppDatePicker
								required
								error={submitted && !!errors?.startDate}
								helperText={submitted && errors?.startDate}
								format="MM/dd/yyyy"
								fullWidth
								label="Start Date"
								placeholder="mm/dd/yyyy"
								onChange={date => setStartDate(date ? moment(date) : moment())}
								value={!disabled ? startDate.toDate() : task?.startOn.toDate()}
								openPickerIcon={<Icons.Calendar size={24} />}
								views={["day"]}
								inputLabelClassName={classes.dateLabel}
								inputClassName={classes.dateInput}
								helperTextClassName={classes.helperText}
							/>

							<AppTimePicker
								required
								error={submitted && !!errors?.startTime}
								helperText={submitted && errors?.startTime}
								format="hh:mm aa"
								ampm={true}
								fullWidth
								label="Start Time"
								placeholder="00:00 AM"
								onChange={time => setStartTime(time ? moment(time) : moment())}
								value={!disabled ? startTime.toDate() : task?.startOn.toDate()}
								openPickerIcon={<Icons.Clock size={24} />}
								views={["hours", "minutes"]}
								inputLabelClassName={classes.dateLabel}
								inputClassName={classes.dateInput}
								helperTextClassName={classes.helperText}
							/>
					</div>

					<div className={classes.dateTimeRow}>
							<AppDatePicker
								required
								minDate={startOn.toDate()}
								error={submitted && !!errors?.dueDate}
								helperText={submitted && errors?.dueDate}
								format="MM/dd/yyyy"
								fullWidth
								label="Due Date"
								placeholder="mm/dd/yyyy"
								onChange={date => setDueDate(date ? moment(date) : null)}
								value={!disabled ? (dueDate ? dueDate.toDate() : null) : task?.endOn.toDate()}
								openPickerIcon={<Icons.Calendar size={24} />}
								views={["day"]}
								inputLabelClassName={classes.dateLabel}
								inputClassName={classes.dateInput}
								helperTextClassName={classes.helperText}
							/>

							<AppTimePicker
								required
								error={submitted && !!(errors?.dueTime || errors?.endOn)}
								helperText={submitted && (errors?.dueTime || errors?.endOn)}
								format="hh:mm aa"
								fullWidth
								label="Due Time"
								placeholder="00:00 AM"
								onChange={time => setDueTime(time ? moment(time) : null)}
								value={!disabled ? (dueTime ? dueTime.toDate() : null) : task?.endOn.toDate()}
								openPickerIcon={<Icons.Clock size={24} />}
								views={["hours", "minutes"]}
								inputLabelClassName={classes.dateLabel}
								inputClassName={classes.dateInput}
								helperTextClassName={classes.helperText}
							/>
					</div>

					<div className={classes.priorityRow}>
						<Mui.Switch
							checked={priority}
							onChange={() => !disabled && setPriority(!priority)}
							disabled={disabled}
							color="default"
							className={classes.switch}
						/>
						<Mui.Typography className={classes.priorityLabel}>
							Mark as priority
						</Mui.Typography>
						<Icons.Flag
							size={24}
							color={theme.palette.primary.main}
							fill={priority ? theme.palette.primary.main : "none"}
						/>
					</div>

					{!disabled && (
						<div className={classes.buttonRow}>
							<Mui.Button
								variant="outlined"
								color="primary"
								onClick={onClose}
								className={classes.cancelButton}
							>
								Cancel
							</Mui.Button>
							<Mui.Button
								variant="contained"
								color="primary"
								onClick={handleSubmit}
								disabled={submitted && !!errors}
								className={classes.saveButton}
							>
								{task ? "Save Task" : "Add Task"}
							</Mui.Button>
						</div>
					)}
				</div>
			</Mui.DialogContent>
		</Mui.Dialog>
	);
};
